import { AxiosError } from 'axios';
import { isEmpty } from 'lodash';
import api from '../../utils/api';
import { alertError } from './alert';
import {
  PRODUCT_FEEDS_DATA,
  PRODUCT_FEEDS_LOADING,
  PRODUCT_FEEDS_PAGE,
  PRODUCT_FEEDS_ROWS_PER_PAGE,
  PRODUCT_FEEDS_TOTAL_COUNT,
  PRODUCT_FEEDS_UPLOAD,
} from '../reducers/productsFeeds';
import { getTotalCountHeader } from './api/utils';
import { uploadFileToPreSignedUrl } from '../../services/upload';
import { CSV_DELIMITER, getCsvHeaders } from '../../utils/csv';
import { buildMapping, buildMappingEntries } from '../../utils/products-feeds';

const setProductsFeedsLoading = (isLoading = true) => ({ type: PRODUCT_FEEDS_LOADING, payload: isLoading });
const setProductsFeedsData = (data) => ({ type: PRODUCT_FEEDS_DATA, payload: data });
const setProductsFeedsTotalCount = (count) => ({ type: PRODUCT_FEEDS_TOTAL_COUNT, payload: count });
export const setProductsFeedsPage = (page) => ({ type: PRODUCT_FEEDS_PAGE, payload: page });
export const setProductsFeedsRowsPerPage = (rows) => ({ type: PRODUCT_FEEDS_ROWS_PER_PAGE, payload: rows });

export const fetchProductsFeeds = () => async (dispatch, getState) => {
  dispatch(setProductsFeedsLoading());
  try {
    const { supplierId } = getState().role;
    const tableParams = getState().productsFeeds.table;
    const params = new URLSearchParams({
      limit: tableParams.rowsPerPage,
      page: tableParams.page + 1,
    });
    const response = await api.get(`/suppliers/${supplierId}/product-feeds`, { params });

    if (response?.status === 200) {
      dispatch(setProductsFeedsData(response.data));

      const totalCount = getTotalCountHeader(response);
      dispatch(setProductsFeedsTotalCount(totalCount));
    }
  } catch (error) {
    dispatch(alertError(error));
  }
};

export const uploadProductsFile = (file, onUpload) => async (dispatch) => {
  try {
    const headers = await getCsvHeaders(file, CSV_DELIMITER);
    if (!headers) {
      dispatch(alertError(`Couldn't parse file`, false));
      return;
    }

    if (headers.includes('supplier_id')) {
      dispatch(alertError(`Please remove "supplier_id" column`, false));
      return;
    }

    const mapping = buildMapping(headers);
    validateMappingColumns(mapping);

    const columnEntries = buildMappingEntries(mapping);
    dispatch(uploadProductsToProductAutomation(file, columnEntries, onUpload));
  } catch (e) {
    dispatch(alertError(e, false));
  }
};

export const uploadProductsToProductAutomation = (file, mapping, onUpload) => async (dispatch, getState) => {
  dispatch({ type: PRODUCT_FEEDS_UPLOAD, payload: { isSubmitting: true, hasSubmitted: false } });

  try {
    const { supplierId } = getState().role;

    const { path } = await uploadFileToPreSignedUrl({ supplierId, file, resourceType: 'products' });
    if (!path) {
      dispatch(alertError({ message: 'Error uploading file' }));
      dispatch({ type: PRODUCT_FEEDS_UPLOAD, payload: { isSubmitting: false } });
      return;
    }

    const response = await postProductsToProductAutomation(supplierId, path, mapping);

    if (response.data.success) {
      dispatch({ type: PRODUCT_FEEDS_UPLOAD, payload: { hasSubmitted: true } });
      onUpload();
    } else {
      dispatch(alertError('Error importing products'));
    }
  } catch (error) {
    dispatch({ type: PRODUCT_FEEDS_UPLOAD, payload: { hasSubmitted: false } });
    if (error instanceof AxiosError && error.response?.status === 400 && error.response.data?.errors?.length) {
      const errorMessage = getProductAutomationErrorMessage(error.response.data.errors);
      dispatch(alertError(errorMessage || error, false));
    } else {
      dispatch(alertError(error));
    }
  } finally {
    dispatch({ type: PRODUCT_FEEDS_UPLOAD, payload: { hasSubmitted: true, isSubmitting: false } });
  }
};

async function postProductsToProductAutomation(supplierId, url, fieldMapping) {
  return await api.post(`/product-automation/suppliers/${supplierId}/trigger-ingestion`, {
    url,
    field_mapping: fieldMapping,
  });
}

function getProductAutomationErrorMessage(errors) {
  const columnIndexes = [];
  errors.forEach((dataError) => {
    if (!dataError.errorCode.includes('requestValidation')) {
      return;
    }
    const columnIndex = Number(dataError.path.split('.')[1]) + 1;
    columnIndexes.push(columnIndex);
  });
  if (!columnIndexes.length) return;

  return `Invalid columns: ${columnIndexes.join(', ')}. Please check the column names and try again.`;
}

function getUnusedColumns(mapping) {
  return Object.entries(mapping)
    .map(([key, value]) => (value.destination ? undefined : key))
    .filter(Boolean);
}

function validateMappingColumns(mapping) {
  if (isEmpty(mapping.sku)) {
    throw new Error(`Column "sku" is mandatory.`);
  }
  const unusedColumns = getUnusedColumns(mapping);
  if (unusedColumns.length > 0) {
    throw new Error(`You have unused columns: ${unusedColumns.join(', ')}. Please fix or remove them.`);
  }
}
